import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { trackPageview } from "@/lib/vitrineAnalytics";
import {
  ShieldCheck, BrainCircuit, ActivitySquare, Lock, Globe, BadgeCheck,
  MessageCircle, ArrowRight, ArrowUpRight, Sparkles, Check,
  TrendingUp, Clock, AlertTriangle, Quote, Menu, X, Play, Volume2,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_26d1aa6e-a958-43ca-9116-14748c74a37b/artifacts/lobzyet9_file_00000000236c71fdaa823d03db86abe9.png";
const HERO_TEXTURE = "https://images.unsplash.com/photo-1771013211277-139159840242?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const VISION_BG = "https://images.unsplash.com/photo-1715156153744-d5fd2f1f66eb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const PHARM_1 = "https://customer-assets.emergentagent.com/job_26d1aa6e-a958-43ca-9116-14748c74a37b/artifacts/0zialkm2_1000524127.jpg";
const PHARM_2 = "https://customer-assets.emergentagent.com/job_26d1aa6e-a958-43ca-9116-14748c74a37b/artifacts/ra4bfyg1_1000524096.jpg";
// Service visuals (Unsplash, pharmacy thematic, editorial grayscale-ready)
const IMG_ASSURANCES = "https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"; // cash / calculator / financial documents — recouvrement & paiements
const IMG_PERIMES = "https://images.unsplash.com/photo-1576602976047-174e57a47881?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"; // medication shelves
const IMG_DISPENSATION = "https://images.unsplash.com/photo-1631549916768-4119b2e5f926?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"; // pharmacist counter consultation
// Video placeholder (Facebook link for now — will be replaced when user uploads the .mp4)
const VIDEO_FACEBOOK_URL = "https://web.facebook.com/share/v/1DNZS7K6Xe/";
const VIDEO_POSTER = "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

// ============ THEME TOKENS ============
// Deep Emerald + Obsidian + Ivory + Gold → "Swiss High-Contrast Editorial"
const NAV_ITEMS = [
  { label: "Produits", to: "#products" },
  { label: "Conformité", to: "#trust" },
  { label: "Tarifs", to: "/tarifs", external: true },
  { label: "Vision", to: "#vision" },
];

const PRODUCTS = [
  {
    id: "assurances",
    name: "PHARMA ASSURANCES",
    tag: "I — RECOUVREMENT",
    desc: "Facturation éclair et recouvrement garanti. La fin des impayés qui dorment chez les assureurs.",
    bullets: ["Dépôt cacheté OCR en 1 scan", "Suivi bordereaux & chèques en temps réel", "Relances automatiques CMU/CNAM"],
    icon: ShieldCheck,
    image: IMG_ASSURANCES,
    span: "md:col-span-8 md:row-span-2",
    big: true,
  },
  {
    id: "perimes",
    name: "PHARMA PÉRIMÉS",
    tag: "II — IA PRÉDICTIVE",
    desc: "L’IA qui voit vos ruptures et péremptions avant qu’elles n’arrivent.",
    bullets: ["Prédiction ruptures par commune", "Alertes J-30/J-60/J-90", "Commande suggérée automatique"],
    icon: BrainCircuit,
    image: IMG_PERIMES,
    span: "md:col-span-4 md:row-span-1",
  },
  {
    id: "dispensation",
    name: "PHARMA DISPENSATION",
    tag: "III — COPILOT OFFICINAL",
    desc: "Le copilote IA qui sécurise chaque dispensation et augmente votre expertise.",
    bullets: ["Interactions DCI temps réel", "Posologie pédiatrique contrôlée", "Base 96 DCI + 119 interactions"],
    icon: ActivitySquare,
    image: IMG_DISPENSATION,
    span: "md:col-span-4 md:row-span-1",
  },
];

const METRICS = [
  { value: "−40%", label: "de ruptures de stock", note: "Dès le premier mois" },
  { value: "−60%", label: "de pertes par péremption", note: "Alertes J-90/60/30" },
  { value: "3×", label: "plus rapide au recouvrement", note: "Bordereaux OCR auto" },
];

const TRUST_BADGES = [
  { icon: BadgeCheck, label: "Conforme aux normes pharmaceutiques ivoiriennes" },
  { icon: Lock, label: "Chiffrement AES-256 · Secret médical garanti" },
  { icon: Globe, label: "Hébergement sécurisé en Afrique" },
  { icon: ShieldCheck, label: "Validée par des pharmaciens praticiens CI" },
];

const TESTIMONIALS = [
  {
    name: "Dr. AMOAKON",
    photo: PHARM_1,
    quote: "En 3 mois, nos bordereaux passent en 48 h au lieu de 3 semaines. La trésorerie de l’officine a changé de visage.",
    meta: "Pharmacien titulaire · Pharmacie Saint-Étienne, Yopougon",
  },
  {
    name: "Dr. LOBA",
    photo: PHARM_2,
    quote: "L’IA nous a évité deux péremptions majeures sur le paracétamol enfant. C’est devenu un outil de décision quotidien.",
    meta: "Pharmacien titulaire · Pharmacie Magnificat, Abobo",
  },
];

const VISION_CHAPTERS = [
  { year: "2026", title: "Domination Abidjan", desc: "500 pharmacies actives. 20 cas clients publics. Intégration native CMU/CNAM." },
  { year: "2027", title: "Extension nationale", desc: "1 200 pharmacies. Bouaké, San-Pedro, Daloa. Lancement du réseau inter-pharmacies." },
  { year: "2028", title: "Afrique de l’Ouest", desc: "Sénégal, Burkina Faso, Mali, Togo. Premier acteur SaaS pharmaceutique panafricain francophone." },
  { year: "2031", title: "Santé publique panafricaine", desc: "5 000+ officines. Partenariat institutionnel OMS / UEMOA." },
];

// ============ COMPONENTS ============

function Header({ scrolled }) {
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <header
      data-testid="landing-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "bg-[#F9F8F6]/85 backdrop-blur-xl border-b border-[#E5E4E2]" : "bg-transparent border-b border-transparent"}`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="brand-logo-link">
          <img src={LOGO_URL} alt="CMES" className="h-9 w-9 object-contain" />
          <div className="leading-tight">
            <p className="font-serif text-[17px] tracking-tight text-[#0A0A0A] font-semibold">CMES Pharma Intel</p>
            <p className="text-[9px] uppercase tracking-[0.2em] text-[#4A4D4C]">Institution officinale</p>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV_ITEMS.map((n) => (
            n.external ? (
              <Link key={n.to} to={n.to} className="text-[13px] font-medium text-[#0A0A0A]/75 hover:text-[#025946] transition-colors relative group">
                {n.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#025946] group-hover:w-full transition-all duration-300"></span>
              </Link>
            ) : (
              <a key={n.to} href={n.to} className="text-[13px] font-medium text-[#0A0A0A]/75 hover:text-[#025946] transition-colors relative group">
                {n.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#025946] group-hover:w-full transition-all duration-300"></span>
              </a>
            )
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a href="https://wa.me/2250768127000" target="_blank" rel="noopener noreferrer" data-testid="header-whatsapp-btn"
            className="w-10 h-10 rounded-full border border-[#E5E4E2] hover:border-[#025946] hover:text-[#025946] flex items-center justify-center transition-colors">
            <MessageCircle className="w-4 h-4" />
          </a>
          <button onClick={() => nav("/login")} data-testid="header-login-btn"
            className="px-5 py-2.5 text-[13px] font-semibold text-[#0A0A0A] hover:text-[#025946] transition-colors">
            Espace Pharmacien
          </button>
          <button onClick={() => nav("/register")} data-testid="header-cta-btn"
            className="group px-6 py-2.5 bg-[#0A0A0A] hover:bg-[#025946] text-white text-[13px] font-semibold flex items-center gap-2 transition-colors">
            Essai 30 jours
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        <button className="lg:hidden" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn">
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {menuOpen && (
        <div className="lg:hidden bg-[#F9F8F6] border-t border-[#E5E4E2] px-6 py-6 space-y-4">
          {NAV_ITEMS.map((n) => (
            n.external ? (
              <Link key={n.to} to={n.to} onClick={() => setMenuOpen(false)} className="block text-sm font-medium text-[#0A0A0A]">{n.label}</Link>
            ) : (
              <a key={n.to} href={n.to} onClick={() => setMenuOpen(false)} className="block text-sm font-medium text-[#0A0A0A]">{n.label}</a>
            )
          ))}
          <button onClick={() => nav("/register")} className="w-full px-6 py-3 bg-[#0A0A0A] text-white text-sm font-semibold">Essai 30 jours</button>
        </div>
      )}
    </header>
  );
}

function Hero() {
  const nav = useNavigate();
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 0.3], [0, -50]);

  return (
    <section className="relative min-h-[100vh] bg-[#F9F8F6] overflow-hidden" data-testid="hero-section">
      {/* Background texture */}
      <motion.div
        style={{ y, backgroundImage: `url(${HERO_TEXTURE})` }}
        className="absolute inset-0 bg-cover bg-center opacity-[0.08] mix-blend-multiply pointer-events-none"
      />
      {/* Grid lines */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: "linear-gradient(to right, #0A0A0A 1px, transparent 1px), linear-gradient(to bottom, #0A0A0A 1px, transparent 1px)",
        backgroundSize: "80px 80px",
        opacity: 0.015
      }}></div>

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 pt-40 pb-24 lg:pt-48 lg:pb-32">
        <div className="grid grid-cols-12 gap-6">
          {/* Left: content */}
          <div className="col-span-12 lg:col-span-8">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-8"
            >
              ─── L’institution technologique des officines
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif text-[52px] sm:text-[72px] lg:text-[96px] leading-[0.95] tracking-[-0.02em] text-[#0A0A0A] mb-8"
            >
              Ne gérez plus <br />
              votre pharmacie.<br />
              <span className="italic font-light text-[#025946]">Dirigez-la.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="max-w-[580px] text-[17px] lg:text-[19px] leading-[1.7] text-[#4A4D4C] mb-12 font-light"
            >
              Le partenaire stratégique de santé publique des pharmaciens ivoiriens.
              Gagnez du temps, sécurisez votre conformité, éliminez les pertes &mdash;
              et retrouvez <span className="text-[#0A0A0A] font-normal">votre tranquillité d’esprit</span>.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <button onClick={() => nav("/register")} data-testid="hero-cta-button"
                className="group px-8 py-4 bg-[#0A0A0A] hover:bg-[#025946] text-white text-[14px] font-semibold flex items-center justify-center gap-3 transition-all">
                Découvrir l’Intelligence Officinale
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </button>
              <a href="https://wa.me/2250768127000" target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn"
                className="group px-8 py-4 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white text-[14px] font-semibold flex items-center justify-center gap-3 transition-all">
                <MessageCircle className="w-5 h-5" />
                Parler à un expert · 7j/7
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1 }}
              className="mt-16 flex flex-wrap gap-x-8 gap-y-3 text-[11px] font-medium text-[#4A4D4C]"
            >
              <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#025946]" /> Essai gratuit 30 jours</span>
              <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#025946]" /> Sans engagement</span>
              <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#025946]" /> Mobile Money accepté</span>
              <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#025946]" /> Formation incluse</span>
            </motion.div>
          </div>

          {/* Right: decorative */}
          <div className="hidden lg:block lg:col-span-4 relative">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.5, delay: 0.8 }}
              className="relative"
            >
              {/* Vertical accent line */}
              <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#025946] to-transparent"></div>

              {/* Floating metric card */}
              <div className="ml-12 mt-16 bg-[#0A0A0A] text-[#F9F8F6] p-8">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-2 h-2 rounded-full bg-[#C9A25E] animate-pulse"></div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A25E]">Temps réel</p>
                </div>
                <p className="text-[11px] uppercase tracking-wider text-white/50 mb-2">Recouvrement ce trimestre</p>
                <p className="font-serif text-5xl font-light mb-6">847M <span className="text-[#C9A25E]">FCFA</span></p>
                <div className="h-px bg-white/10 mb-6"></div>
                <div className="grid grid-cols-2 gap-4 text-[11px]">
                  <div>
                    <p className="text-white/50 uppercase tracking-wider">Bordereaux traités</p>
                    <p className="font-serif text-2xl font-light mt-1">2 413</p>
                  </div>
                  <div>
                    <p className="text-white/50 uppercase tracking-wider">Ruptures évitées</p>
                    <p className="font-serif text-2xl font-light mt-1 text-[#037A60]">186</p>
                  </div>
                </div>
              </div>

              {/* Institutional stamp */}
              <div className="ml-12 mt-6 p-4 border border-[#E5E4E2] bg-white/60 backdrop-blur-sm">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="w-5 h-5 text-[#025946] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#025946] font-bold">Certification interne</p>
                    <p className="text-[11px] text-[#4A4D4C] mt-1">Données patients chiffrées AES-256 · Hébergement Afrique · Audit trimestriel</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <div className="w-px h-12 bg-[#0A0A0A]/30"></div>
        <p className="text-[9px] uppercase tracking-[0.3em] text-[#0A0A0A]/50">Descendre</p>
      </div>
    </section>
  );
}

function PainPoints() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const pains = [
    { icon: TrendingUp, title: "Ruptures invisibles", desc: "Vous découvrez la rupture au comptoir. Trop tard." },
    { icon: AlertTriangle, title: "Pertes massives", desc: "Les médicaments périment avant d’être détectés." },
    { icon: Clock, title: "Recouvrement lent", desc: "3 semaines minimum pour un bordereau CMU. Trésorerie étranglée." },
    { icon: ShieldCheck, title: "Risques réglementaires", desc: "La conformité évolue vite. Vos process, moins." },
  ];
  return (
    <section ref={ref} className="relative bg-[#0A0A0A] text-[#F9F8F6] py-28 lg:py-40 overflow-hidden" data-testid="painpoints-section">
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: "radial-gradient(circle at 20% 50%, #C9A25E 1px, transparent 1px)",
        backgroundSize: "40px 40px"
      }}></div>
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-12 gap-6 mb-20">
          <div className="col-span-12 lg:col-span-5">
            <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#C9A25E] mb-6">─── Ce qui vous épuise</p>
            <h2 className="font-serif text-4xl lg:text-6xl leading-[1.05] tracking-tight">
              Vos journées ne devraient pas ressembler <em className="text-[#C9A25E] font-light">à ça</em>.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-[15px] leading-[1.7] text-white/60 font-light">
              Chaque jour, entre deux patients, vous portez la charge mentale d’une trésorerie instable, de stocks imprévisibles et d’une réglementation mouvante.
              CMES vous libère, une tâche à la fois.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
          {pains.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="bg-[#0A0A0A] p-8 lg:p-10 group hover:bg-[#121212] transition-colors" data-testid={`pain-card-${i}`}
            >
              <p.icon className="w-7 h-7 text-[#C9A25E] mb-6" strokeWidth={1.2} />
              <h3 className="font-serif text-2xl font-light mb-3">{p.title}</h3>
              <p className="text-[13px] text-white/50 leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductsBento() {
  const nav = useNavigate();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <section ref={ref} id="products" className="relative bg-[#F9F8F6] py-28 lg:py-40" data-testid="products-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-12 gap-6 mb-20">
          <div className="col-span-12 lg:col-span-5">
            <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-6">─── Notre Trilogie</p>
            <h2 className="font-serif text-4xl lg:text-6xl leading-[1.05] tracking-tight text-[#0A0A0A]">
              Trois modules. <br /><em className="italic font-light">Un seul objectif :</em> <br />votre autorité.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-[15px] leading-[1.7] text-[#4A4D4C] font-light">
              Chacun répond à une douleur quotidienne spécifique. Ensemble, ils constituent le système nerveux digital de votre officine.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 lg:gap-6 auto-rows-[minmax(280px,auto)]">
          {PRODUCTS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 40 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                className={`group relative ${p.span} col-span-12 border border-[#E5E4E2] bg-white hover:border-[#025946] transition-all duration-500 flex flex-col cursor-pointer overflow-hidden`}
                onClick={() => nav("/register")}
                data-testid={`product-card-${p.id}`}
              >
                {/* Image header — editorial treatment */}
                {p.image && (
                  <div className={`relative overflow-hidden bg-[#0A0A0A] ${p.big ? 'h-72 lg:h-80' : 'h-40'}`}>
                    <img
                      src={p.image}
                      alt={p.name}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 opacity-80 group-hover:opacity-100 scale-105 group-hover:scale-100 transition-[filter,opacity,transform] duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#025946]/60 via-[#025946]/25 to-transparent mix-blend-multiply"></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/80 via-transparent to-transparent"></div>
                    <div className="absolute top-4 left-4">
                      <p className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-[#C9A25E]">{p.tag}</p>
                    </div>
                    <div className="absolute bottom-4 right-4">
                      <Icon className={`text-[#F9F8F6] ${p.big ? 'w-12 h-12' : 'w-9 h-9'}`} strokeWidth={1.1} />
                    </div>
                  </div>
                )}

                <div className="flex flex-col p-8 lg:p-12 flex-1">
                  {/* Accent corner */}
                  <div className="absolute top-0 right-0 w-16 h-16 border-l border-b border-[#025946]/0 group-hover:border-[#025946]/100 transition-all duration-500"></div>

                  <h3 className={`font-serif font-light leading-[1] tracking-tight text-[#0A0A0A] mb-4 ${p.big ? 'text-5xl lg:text-7xl' : 'text-3xl lg:text-4xl'}`}>
                    {p.name.split(' ').map((w, j) => (
                      <span key={j} className={j === 1 ? "italic" : ""}>{w} </span>
                    ))}
                  </h3>

                  <p className={`text-[#4A4D4C] leading-relaxed mb-6 font-light ${p.big ? 'text-lg lg:text-xl max-w-2xl' : 'text-sm'}`}>{p.desc}</p>

                  <ul className="mt-auto space-y-2">
                    {p.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-[12px] text-[#4A4D4C]">
                        <div className="w-1 h-1 bg-[#025946] rounded-full mt-2 flex-shrink-0"></div>
                        {b}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-6 pt-6 border-t border-[#E5E4E2] flex items-center justify-between group-hover:border-[#025946] transition-colors">
                    <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#0A0A0A] group-hover:text-[#025946] transition-colors">Explorer</p>
                    <ArrowUpRight className="w-5 h-5 text-[#025946] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Metrics() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  return (
    <section ref={ref} className="relative bg-[#0A0A0A] text-[#F9F8F6] py-28 lg:py-40" data-testid="metrics-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#C9A25E] mb-10">─── L’impact mesuré</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
          {METRICS.map((m, i) => (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: i * 0.15 }}
              className={`py-8 lg:py-10 ${i < 2 ? "md:border-r border-white/10" : ""} ${i > 0 ? "md:pl-10" : ""}`}
            >
              <p className="font-serif text-7xl lg:text-[140px] font-light leading-none tracking-[-0.03em] mb-6 text-[#F9F8F6]">{m.value}</p>
              <p className="text-lg font-medium text-[#F9F8F6] mb-1">{m.label}</p>
              <p className="text-[12px] uppercase tracking-wider text-white/40">{m.note}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Trust() {
  return (
    <section id="trust" className="relative bg-[#F9F8F6] py-24 lg:py-32 border-y border-[#E5E4E2]" data-testid="trust-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-4 mb-10 lg:mb-0">
            <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-6">─── Confiance & Conformité</p>
            <h2 className="font-serif text-3xl lg:text-5xl leading-[1.1] tracking-tight text-[#0A0A0A]">
              Une institution, <br />pas un logiciel.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-7 lg:col-start-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E5E4E2]">
              {TRUST_BADGES.map((b, i) => (
                <div key={i} className="bg-[#F9F8F6] p-8 flex items-start gap-4" data-testid={`trust-badge-${i}`}>
                  <b.icon className="w-5 h-5 text-[#025946] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <p className="text-[13px] leading-[1.5] text-[#0A0A0A] font-medium">{b.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function VideoShowcase() {
  return (
    <section id="video" className="relative bg-[#0A0A0A] text-[#F9F8F6] py-28 lg:py-40 overflow-hidden" data-testid="video-section">
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: "radial-gradient(circle at 80% 20%, #C9A25E 1px, transparent 1px)",
        backgroundSize: "50px 50px"
      }}></div>
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-12 gap-6 mb-14">
          <div className="col-span-12 lg:col-span-6">
            <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#C9A25E] mb-6">─── Voir la solution en action</p>
            <h2 className="font-serif text-4xl lg:text-6xl leading-[1.05] tracking-tight">
              Une démonstration <br /><em className="italic font-light text-[#C9A25E]">vaut mille mots.</em>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-[15px] leading-[1.7] text-white/60 font-light">
              Découvrez en 2 minutes comment CMES Pharma Intel transforme le quotidien d’une officine : recouvrement express, IA anti-péremption, dispensation sécurisée.
            </p>
          </div>
        </div>

        <a href={VIDEO_FACEBOOK_URL} target="_blank" rel="noopener noreferrer" data-testid="video-showcase-cta"
          className="group relative block aspect-video w-full border border-white/10 overflow-hidden cursor-pointer">
          {/* Poster */}
          <img src={VIDEO_POSTER} alt="Démonstration CMES Pharma Intel" className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 opacity-50 group-hover:opacity-80 transition-[filter,opacity] duration-700" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#025946]/40 via-transparent to-[#0A0A0A]/70"></div>
          {/* Play button */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="group/btn relative">
              <div className="absolute inset-0 rounded-full bg-[#C9A25E]/30 animate-ping"></div>
              <div className="relative w-20 h-20 lg:w-28 lg:h-28 rounded-full bg-[#C9A25E] flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-500">
                <Play className="w-8 h-8 lg:w-10 lg:h-10 text-[#0A0A0A] ml-1" fill="#0A0A0A" />
              </div>
            </div>
          </div>
          {/* Bottom caption */}
          <div className="absolute bottom-0 inset-x-0 p-6 lg:p-10 flex items-end justify-between">
            <div>
              <p className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-[#C9A25E] mb-2">Démonstration officielle</p>
              <p className="font-serif text-xl lg:text-2xl font-light">CMES Pharma Intel · Le film</p>
            </div>
            <div className="flex items-center gap-2 text-[11px] font-medium text-white/70 uppercase tracking-wider">
              <Volume2 className="w-4 h-4" />
              <span>Durée · 2 min</span>
            </div>
          </div>
          {/* Hover overlay */}
          <div className="absolute inset-0 border-2 border-transparent group-hover:border-[#C9A25E]/50 transition-colors duration-500 pointer-events-none"></div>
        </a>

        <p className="mt-6 text-center text-[11px] text-white/40 font-light">
          La vidéo s’ouvre dans un nouvel onglet. Bientôt disponible en lecture intégrée sur le site.
        </p>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="relative bg-[#F9F8F6] py-28 lg:py-40" data-testid="testimonials-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="mb-16">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-6">─── La parole des praticiens</p>
          <h2 className="font-serif text-4xl lg:text-6xl leading-[1.05] tracking-tight text-[#0A0A0A] max-w-3xl">
            Ils parlent <em className="italic font-light text-[#025946]">comme vous.</em>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10">
          {TESTIMONIALS.map((t, i) => (
            <div key={t.name} className="group border border-[#E5E4E2] bg-white p-8 lg:p-12 hover:border-[#025946] hover:shadow-2xl hover:shadow-[#025946]/5 transition-all duration-500" data-testid={`testimonial-${i}`}>
              <Quote className="w-8 h-8 text-[#C9A25E] mb-6" strokeWidth={1} />
              <p className="font-serif text-2xl lg:text-3xl leading-[1.3] text-[#0A0A0A] font-light mb-8">"{t.quote}"</p>
              <div className="flex items-center gap-4 pt-6 border-t border-[#E5E4E2]">
                <div className="relative flex-shrink-0">
                  <img
                    src={t.photo}
                    alt={t.name}
                    className="w-16 h-16 rounded-full object-cover grayscale group-hover:grayscale-0 transition-[filter] duration-500"
                    style={{ objectPosition: "center 25%" }}
                  />
                  <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-[#025946] border-2 border-white rounded-full flex items-center justify-center">
                    <BadgeCheck className="w-3 h-3 text-white" strokeWidth={2.5} />
                  </span>
                </div>
                <div>
                  <p className="text-[14px] font-semibold text-[#0A0A0A]">{t.name}</p>
                  <p className="text-[11px] uppercase tracking-wider text-[#4A4D4C] mt-0.5">{t.meta}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Vision() {
  return (
    <section id="vision" className="relative bg-[#0A0A0A] text-[#F9F8F6] py-28 lg:py-44 overflow-hidden" data-testid="vision-section">
      <div className="absolute inset-0 opacity-25" style={{ backgroundImage: `url(${VISION_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}></div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/60"></div>

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-12 gap-6 mb-20">
          <div className="col-span-12 lg:col-span-6">
            <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#C9A25E] mb-6">─── Horizon 2031</p>
            <h2 className="font-serif text-4xl lg:text-7xl leading-[1.05] tracking-tight">
              De l’officine <br /><em className="italic font-light text-[#C9A25E]">à la santé publique</em> <br />panafricaine.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-[15px] leading-[1.7] text-white/60 font-light">
              Nous ne construisons pas un produit. Nous construisons l’infrastructure numérique de la santé pharmaceutique en Afrique francophone.
              Chaque officine connectée nous rapproche d’un continent qui se soigne mieux.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-white/10">
          {VISION_CHAPTERS.map((c, i) => (
            <div key={c.year} className="bg-[#0A0A0A] p-8 lg:p-10" data-testid={`vision-chapter-${i}`}>
              <p className="font-serif text-5xl lg:text-6xl font-light text-[#C9A25E] mb-6 tracking-tight">{c.year}</p>
              <h3 className="text-lg font-semibold mb-3">{c.title}</h3>
              <p className="text-[12px] text-white/50 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  const nav = useNavigate();
  return (
    <section id="pricing" className="relative bg-[#F9F8F6] py-28 lg:py-40" data-testid="cta-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="border-t border-b border-[#0A0A0A] py-16 lg:py-24 px-6 lg:px-16 relative">
          {/* Corner decorations */}
          <div className="absolute top-0 left-0 w-4 h-4 border-l border-t border-[#025946] -translate-x-[1px] -translate-y-[1px]"></div>
          <div className="absolute top-0 right-0 w-4 h-4 border-r border-t border-[#025946] translate-x-[1px] -translate-y-[1px]"></div>
          <div className="absolute bottom-0 left-0 w-4 h-4 border-l border-b border-[#025946] -translate-x-[1px] translate-y-[1px]"></div>
          <div className="absolute bottom-0 right-0 w-4 h-4 border-r border-b border-[#025946] translate-x-[1px] translate-y-[1px]"></div>

          <div className="grid grid-cols-12 gap-6 items-center">
            <div className="col-span-12 lg:col-span-7">
              <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-6">─── Commencez maintenant</p>
              <h2 className="font-serif text-4xl lg:text-6xl leading-[1.05] tracking-tight text-[#0A0A0A] mb-6">
                L’essai est gratuit. <br /><em className="italic font-light">La transformation, immédiate.</em>
              </h2>
              <p className="text-[15px] text-[#4A4D4C] leading-relaxed max-w-2xl font-light">
                Rejoignez les premières officines qui dirigent leur pharmacie au lieu de la subir.
                30 jours d’essai complet. Mobile Money accepté. Accompagnement personnel.
              </p>
            </div>
            <div className="col-span-12 lg:col-span-5 flex flex-col gap-4 lg:items-end">
              <div className="text-right lg:text-right">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#4A4D4C] mb-2">À partir de</p>
                <p className="font-serif text-5xl lg:text-6xl font-light text-[#0A0A0A]">
                  25 000 <span className="text-2xl text-[#4A4D4C]">FCFA/mois</span>
                </p>
                <p className="text-[11px] text-[#4A4D4C] mt-2">MTN · Orange · Moov · Virement</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <button onClick={() => nav("/register")} data-testid="cta-start-btn"
                  className="group px-8 py-4 bg-[#0A0A0A] hover:bg-[#025946] text-white text-[14px] font-semibold flex items-center justify-center gap-3 transition-all">
                  Commencer l’essai
                  <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </button>
                <Link to="/tarifs" data-testid="cta-pricing-btn"
                  className="px-8 py-4 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white text-[14px] font-semibold flex items-center justify-center gap-3 transition-all">
                  Voir les tarifs détaillés
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0A0A0A] text-[#F9F8F6] pt-20 pb-10" data-testid="landing-footer">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-12 gap-8 mb-16">
          <div className="col-span-12 lg:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <img src={LOGO_URL} alt="CMES" className="h-10 w-10 object-contain" />
              <div>
                <p className="font-serif text-xl font-semibold">CMES Pharma Intel</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/50">Institution officinale</p>
              </div>
            </div>
            <p className="text-[13px] text-white/60 leading-[1.7] max-w-sm mb-6">
              Le partenaire stratégique de santé publique des pharmaciens d’Afrique francophone. Abidjan, Côte d’Ivoire.
            </p>
            <div className="flex items-center gap-3">
              <a href="https://wa.me/2250768127000" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-[#C9A25E] hover:text-[#C9A25E] transition-colors">
                <MessageCircle className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="col-span-6 lg:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A25E] font-bold mb-6">Plateforme</p>
            <ul className="space-y-3 text-[13px] text-white/60">
              <li><a href="#products" className="hover:text-white transition-colors">PHARMA ASSURANCES</a></li>
              <li><a href="#products" className="hover:text-white transition-colors">PHARMA PÉRIMÉS</a></li>
              <li><a href="#products" className="hover:text-white transition-colors">PHARMA DISPENSATION</a></li>
            </ul>
          </div>

          <div className="col-span-6 lg:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A25E] font-bold mb-6">Institution</p>
            <ul className="space-y-3 text-[13px] text-white/60">
              <li><a href="#trust" className="hover:text-white transition-colors">Conformité</a></li>
              <li><a href="#vision" className="hover:text-white transition-colors">Vision 2031</a></li>
              <li><Link to="/presentation" className="hover:text-white transition-colors">Présentation</Link></li>
            </ul>
          </div>

          <div className="col-span-6 lg:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A25E] font-bold mb-6">Espace</p>
            <ul className="space-y-3 text-[13px] text-white/60">
              <li><Link to="/login" className="hover:text-white transition-colors">Se connecter</Link></li>
              <li><Link to="/register" className="hover:text-white transition-colors">Essai 30 jours</Link></li>
              <li><a href="https://wa.me/2250768127000" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">WhatsApp 7j/7</a></li>
            </ul>
          </div>

          <div className="col-span-6 lg:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A25E] font-bold mb-6">Contact</p>
            <ul className="space-y-3 text-[13px] text-white/60">
              <li>Abidjan, Côte d’Ivoire</li>
              <li>+225 07 68 12 70 00</li>
              <li>Lun-Dim · 7j/7</li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[11px] text-white/40">© 2026 CMES Pharma Intel. Institution privée de santé digitale.</p>
          <div className="flex items-center gap-6 text-[11px] text-white/40">
            <span className="flex items-center gap-1.5"><Lock className="w-3 h-3" /> AES-256</span>
            <span className="flex items-center gap-1.5"><Globe className="w-3 h-3" /> Afrique</span>
            <span className="flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Fait en Côte d’Ivoire</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FloatingWhatsApp() {
  return (
    <a href="https://wa.me/2250768127000" target="_blank" rel="noopener noreferrer"
      data-testid="whatsapp-support-floating"
      className="fixed bottom-6 left-6 z-40 group flex items-center gap-3 px-5 py-3 bg-[#025946] hover:bg-[#037A60] text-white shadow-2xl transition-all hover:scale-105">
      <MessageCircle className="w-4 h-4" />
      <span className="text-[12px] font-semibold hidden sm:inline">Parler à un expert · 7j/7</span>
    </a>
  );
}

// ============ MAIN ============
export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { trackPageview("/"); }, []);

  return (
    <div className="cmes-landing min-h-screen bg-[#F9F8F6] antialiased" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <Helmet>
        <title>CMES Pharma Intel | Le Partenaire Stratégique des Officines — Côte d'Ivoire</title>
        <meta name="description" content="Plateforme SaaS premium pour officines. Recouvrement assurances, IA anti-péremption, copilot dispensation. Essai 30 jours. Mobile Money accepté." />
        <meta property="og:title" content="CMES Pharma Intel | Institution Officinale Premium" />
        <meta property="og:description" content="Ne gérez plus votre pharmacie. Dirigez-la. Le partenaire stratégique des pharmaciens d'Afrique francophone." />
      </Helmet>
      <style>{`
        html { scroll-behavior: smooth; }
        ::selection { background: #025946; color: #F9F8F6; }
        /* Override global h1-h6 color so landing titles inherit from their parent */
        .cmes-landing h1,
        .cmes-landing h2,
        .cmes-landing h3,
        .cmes-landing h4,
        .cmes-landing h5,
        .cmes-landing h6 {
          font-family: 'Cormorant Garamond', 'Georgia', serif;
          color: inherit;
          letter-spacing: -0.01em;
          font-weight: inherit;
        }
        /* Force white text on dark CTA buttons */
        .cmes-landing .text-white,
        .cmes-landing .text-white * {
          color: #FFFFFF !important;
        }
        /* Section markers */
        .cmes-landing section { isolation: isolate; }
      `}</style>
      <Header scrolled={scrolled} />
      <main>
        <Hero />
        <PainPoints />
        <ProductsBento />
        <Metrics />
        <Trust />
        <VideoShowcase />
        <Testimonials />
        <Vision />
        <CTA />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
